import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useLoaderData, useRevalidator } from 'react-router-dom';
import { apiAdmin } from '@/api/admin';
import { ApiError } from '@/api/errors';
import type {
  EffectiveMemberDto,
  EffectiveMembersResponse,
  OidcAccessScope,
  OidcClientResponse,
  PlatformDefaultsDto,
  ProductDto,
  TenantDto,
} from '@/api/types';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SecretOnceDialog } from '@/components/SecretOnceDialog';
import { useCopy } from '@/hooks/useCopy';
import { useSnackbar } from '@/components/Snackbar';
import { useCurrentOperator } from '@/auth/AuthContext';

export interface OidcClientDetailLoaderData {
  client: OidcClientResponse;
  // SASADM-0108 — the login-tenant label source. This screen never listed
  // tenants; it only ever resolved `client.loginTenantId` to a name, so the read
  // is now the single tenant behind that id rather than the whole table. At most
  // one element (empty for a platform-scoped client, or when the read fails).
  tenants: TenantDto[];
  // Spec 0061 — the product picker source (access-scope chips + the add-product
  // dialog). Resolves `client.allowedProductIds` to display names; degrades to []
  // on error (chips then fall back to `#<id>`).
  products: ProductDto[];
  // Spec 0057 (U2) — the platform auth host is the brand-host DEFAULT placeholder
  // (display-only, like the create wizard). Null when unavailable (pre-0073 /
  // error) → the default brand host renders with a generic caption instead.
  defaults?: PlatformDefaultsDto | null;
}

export async function oidcClientDetailLoader({
  params,
}: {
  params: { id?: string };
}): Promise<OidcClientDetailLoaderData> {
  const id = params.id ?? '';
  const [client, products, defaults] = await Promise.all([
    apiAdmin.oidcClients.get(id),
    apiAdmin.products.list().catch(() => [] as ProductDto[]),
    apiAdmin.platformDefaults.get().catch(() => null),
  ]);
  // The login tenant is known only once the client has loaded, so this read
  // follows rather than joining the parallel batch — a platform-scoped client
  // has no login tenant and issues no request at all. Degrades to [] like the
  // full fetch it replaces; the label then falls back to the login hostname.
  const tenants =
    client.loginTenantId != null
      ? await apiAdmin.tenants
          .get(client.loginTenantId)
          .then((tn) => [tn])
          .catch(() => [] as TenantDto[])
      : [];
  return { client, tenants, products, defaults };
}

export function OidcClientDetail() {
  const { client, tenants, products, defaults } = useLoaderData() as OidcClientDetailLoaderData;
  const { t } = useCopy();
  const snack = useSnackbar();
  const operator = useCurrentOperator();
  const revalidator = useRevalidator();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [confirmDisable, setConfirmDisable] = useState(false);
  const [secret, setSecret] = useState<string | null>(null);
  const [addProductOpen, setAddProductOpen] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<{ id: number; label: string } | null>(null);
  // Spec 0061 — the access-scope control. Initialised from the read's
  // `accessScope`; a local override lets the operator switch axis BEFORE any
  // persistence (there is no client-update endpoint — scope is changed purely by
  // adding/removing products via the allowed-products endpoints):
  //   Platform → Product: reveal the picker so the first product can be added.
  //   Product → Platform: unlock removing the LAST product (the ≥1 guard only
  //     holds while Product-scoped) — clearing the list returns it to Platform.
  // User intent leads, the data follows on revalidate; no resync needed.
  const [scope, setScope] = useState<OidcAccessScope>(client.accessScope);
  // Spec 0038 — derived effective members. undefined = loading; null = unavailable
  // (404 pre-0040) → quiet placeholder.
  const [effective, setEffective] = useState<EffectiveMembersResponse | null | undefined>(
    undefined,
  );
  // Spec 0058 (U2) — per-row grant-toggle state: the in-flight member key (disables
  // its control) and any inline 400/403 surfaced next to that row's toggle.
  const [accessPending, setAccessPending] = useState<string | null>(null);
  const [accessError, setAccessError] = useState<Record<string, string>>({});

  const loadEffective = useCallback(
    () => apiAdmin.oidcClients.effectiveMembers(client.clientDbId).then(setEffective),
    [client.clientDbId],
  );

  useEffect(() => {
    void loadEffective();
  }, [loadEffective]);

  // Spec 0061 — access-scope state. The allow-list is the read's
  // `allowedProductIds` (empty = Platform-scoped); resolve each id to its product
  // for the removable chips (fall back to `#<id>` when the product list is
  // unavailable). The add-product dialog offers products not already allowed.
  const allowedProductIds = client.allowedProductIds ?? [];
  const allowedProducts = allowedProductIds.map((id) => {
    const product = products.find((p) => p.id === id);
    return {
      id,
      label: product ? `${product.displayName} (${product.slug})` : `#${id}`,
    };
  });
  const nonAllowedProducts = products.filter((p) => !allowedProductIds.includes(p.id));

  // Spec 0057 (U2) — render the existing client's login mode + brand-host state
  // from the SASATH-0075/0073 read indicators. Both are PENDING ship, so each
  // falls back to the value already on the response (no dual-shape parsing):
  //   platform-scoped  ⇐ platformScoped, else loginTenantId == null.
  //   brand-host default ⇐ brandHostIsDefault, else no stored brandHost.
  // A DEFAULT brand host renders empty + placeholder — never the resolved value —
  // so a follow-default client is not silently pinned.
  const isPlatformScoped = client.platformScoped ?? client.loginTenantId == null;
  const brandHostIsDefault = client.brandHostIsDefault ?? !client.brandHost;
  const explicitBrandHost = brandHostIsDefault ? null : (client.brandHost ?? null);
  // Spec 0061 (Operator decoupling ruling) — the login tenant is the SEPARATE
  // login-host axis (SASATH-0075), now independent of the removed allowed-tenants
  // list: resolve its label from the ACTIVE tenants, not from the access scope.
  const loginTenant =
    client.loginTenantId != null
      ? tenants.find((tn) => tn.id === client.loginTenantId && tn.status === 'ACTIVE')
      : undefined;
  const loginTenantLabel = loginTenant
    ? `${loginTenant.name} (${loginTenant.slug})`
    : (client.loginHostname ?? (client.loginTenantId != null ? `#${client.loginTenantId}` : ''));

  async function rotate() {
    try {
      const res = await apiAdmin.oidcClients.rotateSecret(client.clientDbId);
      setSecret(res.clientSecret);
      snack.success(t('snackbar.secretRotated', ''));
    } catch (err) {
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
    }
  }

  async function disable() {
    try {
      await apiAdmin.oidcClients.disable(client.clientDbId);
      revalidator.revalidate();
    } catch (err) {
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
    }
    setConfirmDisable(false);
  }

  // Spec 0061 — the access-scope allow-list is persisted via the allowed-products
  // add/remove endpoints (the dominant edit-screen pattern — exactly how this
  // screen managed allowed-tenants; there is no client-update endpoint). Each
  // op revalidates the loader so the chips + `accessScope` reflect the new state.
  async function addProduct(productId: number) {
    try {
      await apiAdmin.oidcClients.addAllowedProduct(client.clientDbId, productId);
      revalidator.revalidate();
    } catch (err) {
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
    }
    setAddProductOpen(false);
  }

  async function removeProduct() {
    if (!confirmRemove) return;
    try {
      await apiAdmin.oidcClients.removeAllowedProduct(client.clientDbId, confirmRemove.id);
      revalidator.revalidate();
    } catch (err) {
      if (err instanceof ApiError) snack.error(t(`errors.codes.${err.code}`, err.code));
    }
    setConfirmRemove(null);
  }

  // Spec 0058 (U2) — client-scoped per-member OIDC grant. NOT_GRANTED/DISABLED →
  // Grant (enabled:true); GRANTED → Disable (enabled:false). The toggle returns no
  // body, so on success we refetch the effective-members read to reflect the new
  // accessState; a 400/403 surfaces inline on the row (the read's display-only
  // resilience is unchanged — a failed read still degrades to the placeholder).
  async function setAccess(m: EffectiveMemberDto, enabled: boolean) {
    const key = `${m.tenantId}-${m.userId}`;
    setAccessError((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setAccessPending(key);
    try {
      await apiAdmin.members.setOidcAccess(m.tenantId, m.userId, client.clientDbId, enabled);
      await loadEffective();
    } catch (err) {
      if (err instanceof ApiError) {
        setAccessError((prev) => ({ ...prev, [key]: t(`errors.codes.${err.code}`, err.code) }));
      }
    } finally {
      setAccessPending(null);
    }
  }

  return (
    <Box>
      <Breadcrumbs
        items={[
          { label: t('nav.home', 'Home'), to: '/' },
          { label: t('nav.reference', 'Reference') },
          { label: t('nav.oidcClients', 'OIDC clients'), to: '/reference/oidc-clients' },
          { label: client.clientName },
        ]}
      />
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h1">{client.clientName}</Typography>
          <Typography
            sx={{
              color: 'text.secondary',
            }}
          >
            {client.clientId}
          </Typography>
        </Box>
        {operator?.isSuperAdmin ? (
          <Box>
            <Button variant="outlined" onClick={(e) => setMenuAnchor(e.currentTarget)}>
              {t('common.actions', 'Actions')}
            </Button>
            <Menu
              open={Boolean(menuAnchor)}
              anchorEl={menuAnchor}
              onClose={() => setMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setMenuAnchor(null);
                  void rotate();
                }}
              >
                {t('oidc.detail.rotate', 'Rotate secret')}
              </MenuItem>
              {client.enabled !== false ? (
                <MenuItem
                  onClick={() => {
                    setMenuAnchor(null);
                    setConfirmDisable(true);
                  }}
                >
                  {t('oidc.detail.disable', 'Disable')}
                </MenuItem>
              ) : null}
            </Menu>
          </Box>
        ) : null}
      </Stack>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Chip label={client.integrationType} />
        <Chip
          label={client.enabled === false ? 'Disabled' : 'Active'}
          color={client.enabled === false ? 'default' : 'success'}
        />
      </Stack>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        {/* Semantic h2 (visually h3) — first subsection under the h1 page title,
            so the heading order is valid (the sibling sections below stay h3). */}
        <Typography variant="h3" component="h2" gutterBottom>
          {t('oidc.detail.loginBrand', 'Login & brand host')}
        </Typography>
        <Stack spacing={1.5}>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {t('oidc.detail.loginMode', 'Login mode')}
            </Typography>
            {isPlatformScoped ? (
              <>
                <Typography variant="body2">
                  {t('oidc.detail.loginModePlatform', 'Platform-scoped')}
                </Typography>
                <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                  {t(
                    'oidc.detail.loginModePlatformHelp',
                    'Auth across the allowed tenants on the platform login host — no single login tenant.',
                  )}
                </Typography>
              </>
            ) : (
              <Typography variant="body2">
                {t('oidc.detail.loginModeTenant', 'Tenant-scoped')} — {loginTenantLabel}
              </Typography>
            )}
          </Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {t('oidc.detail.brandHost', 'Brand host')}
            </Typography>
            {explicitBrandHost ? (
              <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {explicitBrandHost}
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
                {defaults?.authHostname ? (
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: 'monospace', color: 'text.secondary' }}
                  >
                    {defaults.authHostname}
                  </Typography>
                ) : null}
                <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                  {t('oidc.detail.brandHostDefault', 'Platform default (follows public origin)')}
                </Typography>
              </Stack>
            )}
          </Box>
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h3" gutterBottom>
          {t('oidc.detail.scopes', 'Scopes')}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap' }}>
          {client.scopes.map((s) => (
            <Chip key={s} label={s} size="small" />
          ))}
        </Stack>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h3" gutterBottom>
          {t('oidc.detail.redirectUris', 'Redirect URIs')}
        </Typography>
        <List dense>
          {client.redirectUris.map((u) => (
            <ListItem key={u}>
              <ListItemText primary={u} slotProps={{ primary: { sx: { fontFamily: 'monospace' } } }} />
            </ListItem>
          ))}
        </List>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h3" gutterBottom>
          {t('oidc.detail.accessScope', 'Access scope')}
        </Typography>
        {operator?.isSuperAdmin ? (
          <FormControl>
            <FormLabel id="oidc-access-scope-label" sx={{ typography: 'caption' }}>
              {t('oidc.detail.accessScope', 'Access scope')}
            </FormLabel>
            <RadioGroup
              aria-labelledby="oidc-access-scope-label"
              value={scope}
              onChange={(e) => setScope(e.target.value as OidcAccessScope)}
            >
              <FormControlLabel
                value="PLATFORM"
                control={<Radio />}
                label={t('oidc.detail.accessScopePlatform', 'Platform-scoped (all products)')}
              />
              <FormControlLabel
                value="PRODUCT"
                control={<Radio />}
                label={t('oidc.detail.accessScopeProduct', 'Product-scoped')}
              />
            </RadioGroup>
            <FormHelperText component="span">
              {scope === 'PLATFORM'
                ? t(
                    'oidc.detail.accessScopePlatformHelp',
                    'All products and their tenants can use this client. The common GHL case.',
                  )
                : t(
                    'oidc.detail.accessScopeProductHelp',
                    "Only the chosen products' tenants can use this client.",
                  )}
            </FormHelperText>
          </FormControl>
        ) : (
          <Typography variant="body2">
            {scope === 'PLATFORM'
              ? t('oidc.detail.accessScopePlatform', 'Platform-scoped (all products)')
              : t('oidc.detail.accessScopeProduct', 'Product-scoped')}
          </Typography>
        )}
        {/* The picker shows for Product-scoped. The ≥1 rule holds WHILE
            Product-scoped: a chip's Remove is hidden when it is the last product.
            Switching the radio to Platform unlocks removing the remaining
            product(s) (a transitional clearing state) — clearing the list returns
            the client to Platform on the next revalidate. */}
        {scope === 'PRODUCT' ? (
          <Box sx={{ mt: 1.5 }}>
            <Stack
              direction="row"
              sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1 }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {t('oidc.detail.products', 'Products')}
              </Typography>
              {operator?.isSuperAdmin ? (
                <Button size="small" onClick={() => setAddProductOpen(true)}>
                  {t('oidc.detail.addProduct', 'Add product')}
                </Button>
              ) : null}
            </Stack>
            {allowedProducts.length > 0 ? (
              <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                {allowedProducts.map((p) => (
                  <Chip
                    key={p.id}
                    label={p.label}
                    size="small"
                    onDelete={
                      operator?.isSuperAdmin && allowedProductIds.length > 1
                        ? () => setConfirmRemove(p)
                        : undefined
                    }
                  />
                ))}
              </Stack>
            ) : (
              <Alert severity="warning">
                {t('oidc.detail.selectAtLeastOneProduct', 'Select at least one product.')}
              </Alert>
            )}
          </Box>
        ) : allowedProductIds.length > 0 ? (
          <Box sx={{ mt: 1.5 }}>
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
              {allowedProducts.map((p) => (
                <Chip
                  key={p.id}
                  label={p.label}
                  size="small"
                  onDelete={operator?.isSuperAdmin ? () => setConfirmRemove(p) : undefined}
                />
              ))}
            </Stack>
            <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
              {t(
                'oidc.detail.platformClearHint',
                'Remove all products to make this client platform-scoped.',
              )}
            </Typography>
          </Box>
        ) : null}
      </Paper>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="h3" gutterBottom>
          {t('oidc.detail.effectiveMembers', 'Effective members')}
        </Typography>
        {effective === undefined ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('common.loading', 'Loading')}
          </Typography>
        ) : effective === null ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('oidc.detail.effectiveUnavailable', 'Not available yet.')}
          </Typography>
        ) : effective.members.length === 0 ? (
          <Typography
            variant="body2"
            sx={{
              color: 'text.secondary',
            }}
          >
            {t('oidc.detail.effectiveEmpty', 'No members in the allowed tenants yet.')}
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('oidc.detail.effectiveTenant', 'Tenant')}</TableCell>
                  <TableCell>{t('oidc.detail.effectiveMember', 'Member')}</TableCell>
                  <TableCell>{t('oidc.detail.effectiveAccess', 'Access')}</TableCell>
                  <TableCell>{t('members.status', 'Status')}</TableCell>
                  {operator?.isSuperAdmin ? (
                    <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
                  ) : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {effective.members.map((m) => {
                  const key = `${m.tenantId}-${m.userId}`;
                  return (
                    <TableRow key={key} {...(m.eligible ? {} : { sx: { opacity: 0.55 } })}>
                      <TableCell>
                        {m.tenantName} ({m.tenantSlug})
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{m.email}</Typography>
                        <Typography
                          variant="caption"
                          sx={{
                            color: 'text.secondary',
                          }}
                        >
                          {t(`members.roles.${m.role}`, m.role)}
                        </Typography>
                      </TableCell>
                      {/* Spec 0058 (U2) — the three grant states render DISTINCTLY
                          (GRANTED / DISABLED / NOT_GRANTED never collapsed). */}
                      <TableCell>
                        {m.accessState === 'GRANTED' ? (
                          <Typography variant="caption" sx={{ color: 'success.main' }}>
                            {t('oidc.detail.accessGranted', 'Granted')}
                          </Typography>
                        ) : m.accessState === 'DISABLED' ? (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {t('oidc.detail.accessDisabled', 'Disabled')}
                          </Typography>
                        ) : (
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                            {t('oidc.detail.accessNotGranted', 'Not granted')}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {m.eligible ? (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'success.main',
                            }}
                          >
                            {t('oidc.detail.effectiveEligible', 'Eligible')}
                          </Typography>
                        ) : (
                          <Typography
                            variant="caption"
                            sx={{
                              color: 'text.secondary',
                            }}
                          >
                            {!m.membershipActive
                              ? t('oidc.detail.effectiveInactive', 'Membership inactive')
                              : t('oidc.detail.effectiveNotEligible', 'Not eligible')}
                          </Typography>
                        )}
                      </TableCell>
                      {operator?.isSuperAdmin ? (
                        <TableCell align="right">
                          <Stack spacing={0.5} sx={{ alignItems: 'flex-end' }}>
                            {m.accessState === 'GRANTED' ? (
                              <Button
                                size="small"
                                color="error"
                                disabled={accessPending === key}
                                onClick={() => void setAccess(m, false)}
                              >
                                {t('oidc.detail.accessDisable', 'Disable')}
                              </Button>
                            ) : (
                              <Button
                                size="small"
                                disabled={accessPending === key}
                                onClick={() => void setAccess(m, true)}
                              >
                                {t('oidc.detail.accessGrant', 'Grant')}
                              </Button>
                            )}
                            {accessError[key] ? (
                              <Typography variant="caption" sx={{ color: 'error.main' }}>
                                {accessError[key]}
                              </Typography>
                            ) : null}
                          </Stack>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      <Dialog open={addProductOpen} onClose={() => setAddProductOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t('oidc.detail.addProduct', 'Add product')}</DialogTitle>
        <DialogContent>
          <List>
            {nonAllowedProducts.map((p) => (
              <ListItemButton key={p.id} onClick={() => void addProduct(p.id)}>
                <ListItemText primary={p.displayName} secondary={p.slug} />
              </ListItemButton>
            ))}
            {nonAllowedProducts.length === 0 ? (
              <Alert severity="info">
                {t('oidc.detail.allProductsAllowed', 'All products are already allowed.')}
              </Alert>
            ) : null}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddProductOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
        </DialogActions>
      </Dialog>
      <ConfirmDialog
        open={confirmDisable}
        title={t('oidc.detail.disable', 'Disable client?')}
        body="The client will be unable to authenticate users until re-enabled (ops)."
        severity="error"
        confirmLabel={t('oidc.detail.disable', 'Disable')}
        onConfirm={() => void disable()}
        onCancel={() => setConfirmDisable(false)}
      />
      <ConfirmDialog
        open={Boolean(confirmRemove)}
        title={t('oidc.detail.removeProductTitle', 'Remove product?')}
        body={t(
          'oidc.detail.removeProductBody',
          'Remove {{product}} from this client’s allowed products?',
          { product: confirmRemove?.label ?? '' },
        )}
        severity="error"
        confirmLabel={t('common.remove', 'Remove')}
        onConfirm={() => void removeProduct()}
        onCancel={() => setConfirmRemove(null)}
      />
      <SecretOnceDialog
        open={secret !== null}
        secret={secret ?? ''}
        onAcknowledge={() => setSecret(null)}
      />
    </Box>
  );
}
